import React from "react"
import Paper from "@material-ui/core/Paper"
import { makeStyles } from "@material-ui/core/styles"
import Typography from "@material-ui/core/Typography"

import TextField from "@material-ui/core/TextField"
import InputAdornment from "@material-ui/core/InputAdornment"
import Select from "@material-ui/core/Select"
import Grid from "@material-ui/core/Grid"

const useStyles = makeStyles((theme) => ({
  paddedBox: {
    padding: theme.spacing(4, 4),
  },
}))

const GelatinConverter = ({ siteTitle, toggleDrawer }) => {
  const classes = useStyles()
  const gelatinTypes = {
    platinumLeaf: {
      id: "platinumLeaf",
      measurement: "leaf/leaves",
      option: "Platinum leaves",
      bloomStrength: 250,
      unitMass: 1.7,
    },
    platinumLeafGrams: {
      id: "platinumLeafGrams",
      measurement: "grams",
      option: "Platinum leaves in grams",
      bloomStrength: 250,
      unitMass: 1,
    },
    goldLeaf: {
      id: "goldLeaf",
      measurement: "leaf/leaves",
      option: "Gold leaves",
      bloomStrength: 200,
      unitMass: 2,
    },
    goldLeafGrams: {
      id: "goldLeafGrams",
      measurement: "grams",
      option: "Gold leaves in grams",
      bloomStrength: 200,
      unitMass: 1,
    },
    silverLeaf: {
      id: "silverLeaf",
      measurement: "leaf/leaves",
      option: "Silver leaves",
      bloomStrength: 160,
      unitMass: 2.5,
    },
    silverLeafGrams: {
      id: "silverLeafGrams",
      measurement: "grams",
      option: "Silver leaves in grams",
      bloomStrength: 160,
      unitMass: 1,
    },
    bronzeLeaf: {
      id: "bronzeLeaf",
      measurement: "leaf/leaves",
      option: "Bronze leaves",
      bloomStrength: 125,
      unitMass: 3.3,
    },
    bronzeLeafGrams: {
      id: "bronzeLeafGrams",
      measurement: "grams",
      option: "Bronze leaves in grams",
      bloomStrength: 125,
      unitMass: 1,
    },
    titaniumLeaf: {
      id: "titaniumLeaf",
      measurement: "leaf/leaves",
      option: "Titanium leaves",
      bloomStrength: 100,
      unitMass: 5,
    },
    titaniumLeafGrams: {
      id: "titaniumLeafGrams",
      measurement: "grams",
      option: "Titanium leaves in grams",
      bloomStrength: 100,
      unitMass: 1,
    },
    gelatinMass: {
      id: "gelatinMass",
      measurement: "grams",
      option: "Gelatin Mass",
      bloomStrength: 200,
      unitMass: 2.0 / 12.0,
    },
  }

  const convertGelatin = (
    fromBloom,
    fromUnits,
    fromUnitMass,
    toBloom,
    toUnitMass
  ) => {
    if (Number.isNaN(fromUnits)) {
      return ""
    }
    const toUnits =
      (fromBloom * fromUnits * fromUnitMass) / toBloom / toUnitMass
    return Math.round((toUnits + Number.EPSILON) * 100) / 100
  }

  const defaultFrom = gelatinTypes["goldLeaf"]
  const defaultTo = gelatinTypes["silverLeafGrams"]
  const defaultFromUnits = 1
  const defaultToUnits = convertGelatin(
    defaultFrom.bloomStrength,
    defaultFromUnits,
    defaultFrom.unitMass,
    defaultTo.bloomStrength,
    defaultTo.unitMass
  )

  const [state, setState] = React.useState({
    from: defaultFrom,
    fromUnits: defaultFromUnits,
    to: defaultTo,
    toUnits: defaultToUnits,
  })

  const handleUnitsChange = (prop) => (event) => {
    const otherProp = prop === "to" ? "from" : "to"
    const input = event.target.value
    const fromUnits = parseFloat(input)
    const gelatinType = state[prop]
    const otherGelatinType = state[otherProp]

    setState({
      ...state,
      [`${prop}Units`]: input,
      [`${otherProp}Units`]: convertGelatin(
        gelatinType.bloomStrength,
        fromUnits,
        gelatinType.unitMass,
        otherGelatinType.bloomStrength,
        otherGelatinType.unitMass
      ),
    })
  }

  const handleChange = (event) => {
    const name = event.target.name
    const gelatinType = gelatinTypes[event.target.value]
    const fromGelatinType = name === "from" ? gelatinType : state.from
    const toGelatinType = name === "to" ? gelatinType : state.to

    setState({
      ...state,
      [name]: gelatinType,
      toUnits: convertGelatin(
        fromGelatinType.bloomStrength,
        state.fromUnits,
        fromGelatinType.unitMass,
        toGelatinType.bloomStrength,
        toGelatinType.unitMass
      ),
    })
  }

  return (
    <Paper className={classes.paddedBox}>
      <Grid container justifyContent="center">
        <Grid item sm={12} md={5}>
          <TextField
            variant="outlined"
            value={state.fromUnits}
            fullWidth
            onChange={handleUnitsChange("from")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {state.from.measurement}
                </InputAdornment>
              ),
            }}
          />
          <Select
            native
            style={{ width: "100%" }}
            value={state.from.id}
            onChange={handleChange}
            inputProps={{
              name: "from",
            }}
          >
            {Object.values(gelatinTypes).map(({ id, option }) => {
              return (
                <option key={id} value={id}>
                  {option}
                </option>
              )
            })}
          </Select>
          <Typography variant="body1">
            (Bloom strength: {state.from.bloomStrength})
          </Typography>
        </Grid>
        <Grid item md={1}>
          <Typography variant="h4" component="p" gutterBottom align="center">
            =
          </Typography>
        </Grid>
        <Grid item sm={12} md={5}>
          <TextField
            variant="outlined"
            value={state.toUnits}
            onChange={handleUnitsChange("to")}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {state.to.measurement}
                </InputAdornment>
              ),
            }}
          />
          <Select
            native
            style={{ width: "100%" }}
            value={state.to.id}
            onChange={handleChange}
            inputProps={{
              name: "to",
            }}
          >
            {Object.values(gelatinTypes).map(({ id, option }) => {
              return (
                <option key={id} value={id}>
                  {option}
                </option>
              )
            })}
          </Select>
          <Typography variant="body1">
            (Bloom strength: {state.to.bloomStrength})
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  )
}
export default GelatinConverter
